import { EXTRACT_BUDGET } from "../../queries";

export interface BudgetItem {
  row: string;
  description: string;
  cost: string;
}

export interface ExtractBudgetResponse {
  success: boolean;
  message: string;
  data?: {
    project_uuid: string;
    project_name: string;
    status: string;
    budget_items: BudgetItem[];
    total_items: number;
    description_column: string;
    cost_column: string;
    error: string | null;
    excel_data?: {
      data: any[][];
      columns: string[];
    };
  };
}

// Helper function to create grid data from budget items
const createGridFromBudgetItems = (budgetItems: BudgetItem[]) => {
  const data: any[][] = [];
  const columns = ['Row', 'Description', 'Cost'];

  budgetItems.forEach(item => {
    const rowNumber = parseInt(item.row.replace('Row_', ''));
    // Ensure we have enough rows
    while (data.length < rowNumber) {
      data.push(['', '', '']);
    }
    // Set the data for this row (using 0-based indexing)
    data[rowNumber - 1] = [item.row, item.description.trim(), item.cost.trim()];
  });

  return {
    data,
    columns
  };
};

/**
 * Converts a file to base64 string
 * @param file - The file to convert
 * @returns Promise<string> - Base64 encoded file content
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Gets the file extension from file type or name
 * @param file - The file to get extension from
 * @returns string - File extension with dot (e.g., ".xlsx")
 */
const getFileExtension = (file: File): string => {
  const typeToExtension: Record<string, string> = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-excel': '.xls',
    'text/csv': '.csv',
    'application/pdf': '.pdf'
  };

  return typeToExtension[file.type] || file.name.substring(file.name.lastIndexOf('.'));
};

/**
 * Extracts budget data from an uploaded file using GraphQL
 * @param projectUuid - The project UUID
 * @param companyId - The company ID
 * @param file - The file to upload
 * @param sheetName - Optional sheet name for Excel files
 * @returns Redux thunk action
 */
export const extractBudget = (
  projectUuid: string,
  companyId: string,
  file: File,
  sheetName?: string | null
) => async (dispatch: any, getState: any): Promise<ExtractBudgetResponse> => {
  try {
    // Get file extension
    const fileExtension = getFileExtension(file);

    // Convert file to base64
    const fileBase64 = await fileToBase64(file);

    // Debug logging
    console.log('=== Budget Extraction Request ===');
    console.log('Company ID:', companyId);
    console.log('Project UUID:', projectUuid);
    console.log('File name:', file.name);
    console.log('File type:', file.type);
    console.log('File extension:', fileExtension);
    console.log('Sheet name:', sheetName);
    console.log('File size:', file.size, 'bytes');
    console.log('Base64 length:', fileBase64.length);
    console.log('===============================');

    // Prepare GraphQL variables - flat structure like generateBatReport
    const variables = {
      cId: companyId,
      pId: projectUuid,
      file: fileBase64,
      file_extension: fileExtension,
      file_name: file.name,
      sheet_name: sheetName || null
    };

    console.log('GraphQL variables:', {
      ...variables,
      file: `${fileBase64.substring(0, 50)}... (${fileBase64.length} chars)`
    });

    // Use standard graphQLFetch like other queries (getBatReport, etc.)
    const { graphQLFetch } = await import('../../../utils');
    const data = await graphQLFetch(
      EXTRACT_BUDGET,
      variables,
      "query",
      getState,
      { fetchPolicy: "no-cache" }
    );

    console.log('GraphQL response:', data);

    // Extract the data from GraphQL response
    const budgetData = data?.extractBudget;

    if (!budgetData) {
      console.error('No data in GraphQL response:', data);
      throw new Error('Budget extraction failed: No data returned from GraphQL query');
    }

    // Check for errors in the response data
    if (budgetData.error) {
      console.error('Backend returned error:', budgetData.error);
      return {
        success: false,
        message: budgetData.error
      };
    }

    // The backend response includes budget items
    if (budgetData.status === 'success') {
      console.log('Budget extraction successful!');
      console.log('Budget items count:', budgetData.budget_items?.length || 0);
      console.log('Description column:', budgetData.description_column);
      console.log('Cost column:', budgetData.cost_column);

      return {
        success: true,
        message: 'Budget extracted successfully',
        data: {
          ...budgetData,
          // Create a simple grid from the budget items
          excel_data: createGridFromBudgetItems(budgetData.budget_items || [])
        }
      };
    } else {
      console.error('Extraction status not success:', budgetData.status);
      return {
        success: false,
        message: budgetData.error || `Failed to extract budget (status: ${budgetData.status})`
      };
    }
  } catch (error) {
    console.error('Error calling extract_budget GraphQL endpoint:');
    console.error('Error type:', error?.constructor?.name);
    console.error('Error message:', error instanceof Error ? error.message : String(error));
    console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    console.error('Full error object:', error);

    // Check if it's a network error
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('Network error - possible CORS issue or network connectivity problem');
      return {
        success: false,
        message: 'Network error: Unable to reach the API. This could be a CORS issue or network connectivity problem.',
      };
    }

    // In development mode, provide mock data
    if (process.env.NODE_ENV === 'development') {
      console.warn('Budget extraction failed in development, using mock data');
      const mockBudgetItems: BudgetItem[] = [
        { row: 'Row_1', description: 'Foundation Work', cost: '$25,000' },
        { row: 'Row_2', description: 'Framing', cost: '$18,000' },
        { row: 'Row_3', description: 'Roofing', cost: '$12,000' },
        { row: 'Row_4', description: 'Electrical', cost: '$8,000' },
        { row: 'Row_5', description: 'Plumbing', cost: '$7,500' },
        { row: 'Row_6', description: 'HVAC', cost: '$9,000' },
        { row: 'Row_7', description: 'Flooring', cost: '$6,000' },
        { row: 'Row_8', description: 'Paint', cost: '$3,000' },
      ];

      return {
        success: true,
        message: 'Budget extracted successfully (mock data)',
        data: {
          project_uuid: projectUuid,
          project_name: `Mock Project ${projectUuid.slice(0, 8)}`,
          status: 'success',
          budget_items: mockBudgetItems,
          total_items: mockBudgetItems.length,
          description_column: 'Col_1',
          cost_column: 'Col_2',
          error: null,
          excel_data: createGridFromBudgetItems(mockBudgetItems)
        }
      };
    }

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
